import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Plus, Info, HelpCircle } from 'lucide-react';

// This is a single textarea component that handles its own state
// to prevent form jumping during typing
const StableTextarea = ({ initialValue, onSave, placeholder, rows = 2 }) => {
  const [value, setValue] = useState(initialValue || '');
  const timeoutRef = useRef(null);
  
  // Update internal state when prop changes (only for external updates)
  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    
    // Debounce the update to parent component
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onSave(newValue);
    }, 500);
  };
  
  return (
    <textarea
      rows={rows}
      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );
};

// List of rhetorical tactics with descriptions and examples
const RHETORICAL_TACTICS = [
  {
    id: 'straw_man',
    name: 'Straw Man',
    description: 'Misrepresenting someone\'s argument to make it easier to attack',
    example: 'Person A: "We should improve healthcare access." Person B: "So you want socialist medicine and government death panels."',
    severity: 'high'
  },
  {
    id: 'false_dilemma',
    name: 'False Dilemma',
    description: 'Presenting only two options when others exist',
    example: 'Either we cut environmental regulations or we lose all our jobs.',
    severity: 'high'
  },
  {
    id: 'ad_hominem',
    name: 'Ad Hominem',
    description: 'Attacking the person instead of addressing their argument',
    example: 'You can\'t trust his economic policy because he was divorced twice.',
    severity: 'medium'
  },
  {
    id: 'appeal_to_emotion',
    name: 'Appeal to Emotion',
    description: 'Using emotion instead of facts to influence an audience',
    example: 'Think of the children! We must pass this law immediately.',
    severity: 'medium'
  },
  {
    id: 'hasty_generalization',
    name: 'Hasty Generalization',
    description: 'Drawing a conclusion based on insufficient or unrepresentative evidence',
    example: 'I met two rude people from that city, so everyone there must be unfriendly.',
    severity: 'medium'
  },
  {
    id: 'appeal_to_authority',
    name: 'Appeal to Authority',
    description: 'Citing an authority figure who isn\'t qualified in the relevant field',
    example: 'This famous actor says this medical treatment works, so it must be effective.',
    severity: 'medium'
  },
  {
    id: 'bandwagon',
    name: 'Bandwagon Fallacy',
    description: 'Appealing to popularity or the fact that many people do something',
    example: 'Everyone is voting for this candidate, so you should too.',
    severity: 'low'
  },
  {
    id: 'slippery_slope',
    name: 'Slippery Slope',
    description: 'Asserting that a small first step will lead to major negative consequences',
    example: 'If we allow this minor regulation, soon we\'ll be living in a totalitarian state.',
    severity: 'high'
  },
  {
    id: 'false_equivalence',
    name: 'False Equivalence',
    description: 'Comparing two things as if they\'re equivalent when they are not',
    example: 'Comparing a minor policy disagreement to historical atrocities.',
    severity: 'high'
  },
  {
    id: 'whataboutism',
    name: 'Whataboutism',
    description: 'Deflecting criticism by pointing to someone else\'s wrongdoing',
    example: 'Why focus on our mistakes when they are doing much worse things?',
    severity: 'high'
  },
  {
    id: 'circular_reasoning',
    name: 'Circular Reasoning',
    description: 'Using the conclusion as part of the premise of an argument',
    example: 'The law is legitimate because it was legally passed.',
    severity: 'medium'
  },
  {
    id: 'loaded_question',
    name: 'Loaded Question',
    description: 'Asking a question that contains a controversial assumption',
    example: 'Have you stopped undermining our democracy yet?',
    severity: 'medium'
  },
  {
    id: 'red_herring',
    name: 'Red Herring',
    description: 'Introducing irrelevant information to distract from the actual issue',
    example: 'Instead of addressing the budget deficit, the politician talked about his opponent\'s vacation home.',
    severity: 'medium'
  },
  {
    id: 'post_hoc',
    name: 'Post Hoc (False Cause)',
    description: 'Assuming that because one event followed another, the first caused the second',
    example: 'I wore my lucky shirt and we won the game, so my shirt caused our victory.',
    severity: 'high'
  },
  {
    id: 'cherry_picking',
    name: 'Cherry Picking',
    description: 'Selecting only evidence that supports your argument while ignoring contrary data',
    example: 'Citing the one study that supports your position while ignoring dozens that contradict it.',
    severity: 'high'
  },
  {
    id: 'appeal_to_ignorance',
    name: 'Appeal to Ignorance',
    description: 'Claiming something is true (or false) because it hasn\'t been proven otherwise',
    example: 'No one has proven that ghosts don\'t exist, therefore they must be real.',
    severity: 'medium'
  },
  {
    id: 'guilt_by_association',
    name: 'Guilt by Association',
    description: 'Discrediting an idea or person based on an association with something unpopular',
    example: 'He once attended the same conference as a controversial figure, so his views must be extreme too.',
    severity: 'medium'
  },
  {
    id: 'tu_quoque',
    name: 'Tu Quoque ("You too")',
    description: 'Deflecting criticism by accusing the accuser of hypocrisy',
    example: 'You criticize me for lying, but you\'ve lied in the past too, so your criticism is invalid.',
    severity: 'high'
  },
  {
    id: 'appeal_to_tradition',
    name: 'Appeal to Tradition',
    description: 'Arguing something must be right because it\'s always been that way',
    example: 'We\'ve always done it this way, so we shouldn\'t change our approach.',
    severity: 'medium'
  },
  {
    id: 'oversimplification',
    name: 'Oversimplification / Reductionism',
    description: 'Reducing a complex issue to a simple cause or solution',
    example: 'The entire economic crisis can be blamed on a single policy decision.',
    severity: 'high'
  }
];

const RhetoricalTacticsEditor = ({ claim, onTacticsChange, existingTactics = [] }) => {
  const [selectedTactics, setSelectedTactics] = useState([]);
  const [tacticsWithNotes, setTacticsWithNotes] = useState({});
  const [showTacticsInfo, setShowTacticsInfo] = useState(false);
  const [error, setError] = useState(null);
  const [customTactic, setCustomTactic] = useState({ name: '', description: '' });

  // Initialize from existing tactics if provided
  useEffect(() => {
    if (existingTactics && existingTactics.length > 0) {
      const tactics = existingTactics.map(t => typeof t === 'string' ? t : t.id || t.name);
      setSelectedTactics(tactics);

      // Initialize notes for tactics if they exist
      const notes = {};
      existingTactics.forEach(t => {
        if (typeof t === 'object' && t.note) {
          notes[t.id || t.name] = t.note;
        }
      });
      setTacticsWithNotes(notes);
    }
  }, [existingTactics]);

  // Notify parent component when tactics change
  useEffect(() => {
    if (onTacticsChange) {
      const tacticsWithData = selectedTactics.map(tacticId => {
        const note = tacticsWithNotes[tacticId] || '';
        const tacticData = RHETORICAL_TACTICS.find(t => t.id === tacticId) || { id: tacticId, name: tacticId };
        return {
          id: tacticId,
          name: tacticData.name,
          note
        };
      });
      onTacticsChange(tacticsWithData);
    }
  }, [selectedTactics, tacticsWithNotes, onTacticsChange]);

  const toggleTactic = (tacticId) => {
    setSelectedTactics(prev => {
      if (prev.includes(tacticId)) {
        return prev.filter(id => id !== tacticId);
      } else {
        return [...prev, tacticId];
      }
    });
    setError(null);
  };

  const updateTacticNote = (tacticId, note) => {
    setTacticsWithNotes(prev => ({
      ...prev,
      [tacticId]: note
    }));
  };

  const addCustomTactic = () => {
    if (!customTactic.name.trim()) {
      setError('Custom tactic name is required');
      return;
    }

    const customId = `custom_${Date.now()}`;
    
    // Add to selected tactics
    setSelectedTactics(prev => [...prev, customId]);
    
    // Add note if provided
    if (customTactic.description) {
      setTacticsWithNotes(prev => ({
        ...prev,
        [customId]: customTactic.description
      }));
    }
    
    // Reset form
    setCustomTactic({ name: '', description: '' });
    setError(null);
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'high': return 'border-red-300 bg-red-50 text-red-800';
      case 'medium': return 'border-orange-300 bg-orange-50 text-orange-800';
      case 'low': return 'border-yellow-300 bg-yellow-50 text-yellow-800';
      default: return 'border-gray-300 bg-gray-50 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-md font-medium text-gray-900">
          Rhetorical Tactics Used
        </h3>
        <button
          type="button"
          onClick={() => setShowTacticsInfo(!showTacticsInfo)}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
        >
          <HelpCircle className="h-4 w-4 mr-1" />
          {showTacticsInfo ? 'Hide Info' : 'What are these?'}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm">
          {error}
        </div>
      )}

      {showTacticsInfo && (
        <div className="bg-blue-50 rounded-lg p-4 shadow-sm mb-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <h4 className="font-medium mb-1">About Rhetorical Tactics</h4>
              <p className="mb-2">
                Rhetorical tactics are persuasive techniques used in communication, often to mislead or 
                persuade without solid evidence. Identifying these tactics helps readers 
                critically evaluate claims and arguments.
              </p>
              <p>
                Select all tactics that apply to the current claim. You can add notes to explain 
                how each tactic is being used in this specific case.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {RHETORICAL_TACTICS.map((tactic) => (
          <div 
            key={tactic.id}
            className={`border rounded-md p-2 cursor-pointer transition-colors ${
              selectedTactics.includes(tactic.id) 
                ? 'border-indigo-500 bg-indigo-50' 
                : 'border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => toggleTactic(tactic.id)}
          >
            <div className="flex justify-between items-start mb-1">
              <span className="font-medium text-sm">{tactic.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${getSeverityClass(tactic.severity)}`}>
                {tactic.severity}
              </span>
            </div>
            <p className="text-xs text-gray-600">{tactic.description}</p>
          </div>
        ))}
      </div>
      
      {/* Notes for selected tactics */}
      {selectedTactics.length > 0 && (
        <div className="mt-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Add Notes (Optional)</h4>
          
          {selectedTactics.map(tacticId => {
            const tactic = RHETORICAL_TACTICS.find(t => t.id === tacticId) || { 
              id: tacticId, 
              name: tacticId.startsWith('custom_') 
                ? tacticsWithNotes[tacticId]?.split(' - ')[0] || 'Custom Tactic' 
                : 'Custom Tactic'
            };
            
            return (
              <div key={tacticId} className="border border-gray-200 rounded-md p-3 bg-white">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">{tactic.name}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTactic(tacticId);
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                
                <StableTextarea
                  initialValue={tacticsWithNotes[tacticId] || ''}
                  onSave={(value) => updateTacticNote(tacticId, value)}
                  placeholder={`Explain how this claim uses the "${tactic.name}" tactic...`}
                />
                
                {tactic.example && (
                  <div className="mt-1 text-xs text-gray-500">
                    <span className="font-medium">Example: </span>
                    {tactic.example}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Add custom tactic */}
      <div className="mt-4 border border-gray-200 rounded-md p-3 bg-gray-50">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Add Custom Tactic</h4>
        
        <div className="space-y-2">
          <input
            type="text"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
            value={customTactic.name}
            onChange={(e) => setCustomTactic(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Custom tactic name"
          />
          
          <StableTextarea
            initialValue={customTactic.description}
            onSave={(value) => setCustomTactic(prev => ({ ...prev, description: value }))}
            placeholder="Description or notes for this custom tactic (optional)"
          />
          
          <button
            type="button"
            onClick={addCustomTactic}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Custom Tactic
          </button>
        </div>
      </div>
    </div>
  );
};

export default RhetoricalTacticsEditor;